import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { EarnedVendorRebatesYearly } from "../../models/earnedVendorRebatesYearly.model";
import { GenericResponseDto } from "../../models/genericResponseDto.model";
import { VendorRebatesAndAmountDto } from "../../models/getVendorRebatesAmountDto.mode";
import { ReconciliationUpdateAPDto } from "../../models/reconciliationUpdateAPRequestDto.model";
import { VendorPurchaseOrderDto } from "../../models/vendorPurchaseOrderDto.model";
import { VendorRebateReconciliationDto } from "../../models/vendorRebateReconciliationDto.model";
import { VendorRebatesRequest } from "../../models/vendorRebatesRequest.model";
import { VendorReconcilationDataRequestDto } from "../../models/vendorReconcilationDataRequestDto.model";
import { VendorReconciliationLogsDto } from "../../models/vendorReconciliationLogsDto";
import { VendorsRebateDetailsRequest } from "../../models/vendorsRebateDetailsRequestDto.model";
import { VendorsRebateDetailsResponse } from "../../models/vendorsRebateDetailsResponseDto.model";

@Injectable({
    providedIn: "root"
})
export class ReportsDashboardService {
    apiBaseUrl = `${environment.apiBaseUrl}/ReportDashboard`;
    httpOptions = {
        headers: new HttpHeaders({ "Content-Type": "application/json" })
    };

    constructor(private httpClient: HttpClient) { }

    getRebatesByAccountNumber(request: VendorRebatesRequest):
    Observable<GenericResponseDto<VendorRebatesAndAmountDto[]>> {
        return this.httpClient.post<GenericResponseDto<VendorRebatesAndAmountDto[]>>(
            `${this.apiBaseUrl}/GetAmountTiers`,
            request,
            this.httpOptions
        );
    }

    getEarnedRebateByYear(year: number):
    Observable<GenericResponseDto<EarnedVendorRebatesYearly>> {
        return this.httpClient.get<GenericResponseDto<EarnedVendorRebatesYearly>>(
            `${this.apiBaseUrl}/GetEarnedRebateByYear?year=${year}`,
            this.httpOptions
        );
    }

    getVendorRebateDetailsList(request: VendorsRebateDetailsRequest):
    Observable<GenericResponseDto<VendorsRebateDetailsResponse[]>> {
        return this.httpClient.post<GenericResponseDto<VendorsRebateDetailsResponse[]>>(
            `${this.apiBaseUrl}/GetVendorRebateDetailsList`,
            request,
            this.httpOptions
        );
    }

    getVendorRebateReconcilationData(request: VendorReconcilationDataRequestDto):
    Observable<GenericResponseDto<VendorRebateReconciliationDto[]>> {
        return this.httpClient.post<GenericResponseDto<VendorRebateReconciliationDto[]>>(
            `${this.apiBaseUrl}/GetAllVendorRebateReconciliations`,
            request,
            this.httpOptions
        );
    }

    reconciliationUpdateAPDetails(request: ReconciliationUpdateAPDto):
    Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/ReconciliationUpdateAP`,
            request,
            this.httpOptions
        );
    }

    emailUnAddedRebates():
    Observable<GenericResponseDto<string | boolean>> {
        return this.httpClient.get<GenericResponseDto<string | boolean>>(
            `${this.apiBaseUrl}/EmailUnAddedRebates`,
            this.httpOptions
        );
    }

    reconciliationUpdateVendorDetails(request: {
        vendorAccountNumber?: string,
        rebateCalculatedVendor?: number,
        year?: number
    }):
        Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/ReconciliationUpdateVendor`,
            request,
            this.httpOptions
        );
    }

    getVendorPurchaseOrderList(
        vendorAccountNumber: string,
        pageSize: number = 10,
        pageNumber: number = 1,
        year?: number
    ): Observable<GenericResponseDto<VendorPurchaseOrderDto[]>> {
        const yearParam = year ? `&year=${year}` : "";
        return this.httpClient.get<GenericResponseDto<VendorPurchaseOrderDto[]>>(
            `${this.apiBaseUrl}/GetVendorPOS?vendorAccountNumber=${vendorAccountNumber}&pageSize=${pageSize}&pageNumber=${pageNumber}${yearParam}`,
            this.httpOptions
        );
    }

    getVendorActivityList(
        vendorAccountNumber: string
    ): Observable<GenericResponseDto<VendorReconciliationLogsDto[]>> {
        return this.httpClient.get<GenericResponseDto<VendorReconciliationLogsDto[]>>(
            `${this.apiBaseUrl}/GetVendorReconciliationLogs?vendorAccountNumber=${vendorAccountNumber}`,
            this.httpOptions
        );
    }

    getVendorReconciliationAmount(
        vendorAccountNumber: string,
        year: number
    ): Observable<GenericResponseDto<number>> {
        return this.httpClient.get<GenericResponseDto<number>>(
            `${this.apiBaseUrl}/GetVendorReconciliationAmount?vendorAccountNumber=${vendorAccountNumber}&year=${year}`,
            this.httpOptions
        );
    }

    exportVendorRebateReconciliationsToExcel(
        request: VendorReconcilationDataRequestDto
    ): Observable<Blob> {
        return this.httpClient.post<Blob>(
            `${this.apiBaseUrl}/ExportVendorRebateReconciliationsToExcel`,
            request,
            { responseType: "blob" as "json" }
        );
    }

    exportVendorRebateDetailsToExcel(
        request: VendorsRebateDetailsRequest
    ): Observable<Blob> {
        return this.httpClient.post<Blob>(
            `${this.apiBaseUrl}/ExportVendorRebateDetailsToExcel`,
            request,
            { responseType: "blob" as "json" }
        );
    }

    /**
     * Add vendor excluded purchase orders in bulk
     * @param request The excluded PO details with list of purchase orders
     * @returns Observable with the operation result
     */
    addVendorExcludedPO(request: {
        vendorAccountNumber?: string,
        purchaseOrders?: string[]
    }): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/AddVendorExcludedPO`,
            request,
            this.httpOptions
        );
    }

    /**
     * Add vendor excluded purchase orders for multiple vendors in bulk
     * @param request The excluded PO details with list of vendors and their purchase orders
     * @returns Observable with the operation result
     */
    addMultiVendorExcludedPOs(request: {
        vendors: Array<{
            vendorAccountNumber?: string,
            purchaseOrders?: string[]
        }>,
        year?: number
    }): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/AddMultiVendorExcludedPOs`,
            request,
            this.httpOptions
        );
    }

    /**
     * Delete a vendor excluded purchase order (soft delete)
     * @param vendorAccountNumber The vendor account number
     * @param purchaseOrder The purchase order number
     * @param year Optional year for recalculation
     * @returns Observable with the operation result
     */
    deleteVendorExcludedPO(vendorAccountNumber: string, purchaseOrder: string, year?: number):
    Observable<GenericResponseDto<boolean>> {
        const yearParam = year ? `&year=${year}` : "";
        return this.httpClient.delete<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/DeleteVendorExcludedPO?vendorAccountNumber=${encodeURIComponent(vendorAccountNumber)}&purchaseOrder=${encodeURIComponent(purchaseOrder)}${yearParam}`,
            this.httpOptions
        );
    }

    /**
     * Get all vendor excluded purchase orders
     * @param vendorAccountNumber Optional vendor account number
     * @param year Optional year filter
     * @returns Observable with list of excluded purchase orders
     */
    getVendorExcludedPOs(vendorAccountNumber?: string, year?: number): Observable<GenericResponseDto<Array<{
        id: number,
        vendorAccountNumber?: string,
        purchaseOrder?: string,
        year: number
    }>>> {
        const params = new URLSearchParams();
        if (vendorAccountNumber) {
            params.append("vendorAccountNumber", vendorAccountNumber);
        }
        if (year) {
            params.append("year", year.toString());
        }
        const queryString = params.toString() ? `?${params.toString()}` : "";
        return this.httpClient.get<GenericResponseDto<Array<{
            id: number,
            vendorAccountNumber?: string,
            purchaseOrder?: string,
            year: number
        }>>>(
            `${this.apiBaseUrl}/GetVendorExcludedPOs${queryString}`,
            this.httpOptions
        );
    }

    /**
     * Get vendor main contacts for sending emails
     * @param vendorAccountNumbers List of vendor account numbers
     * @returns Observable with array of vendor contacts
     */
    getVendorMainContacts(vendorAccountNumbers: string[]):
    Observable<GenericResponseDto<Array<{
        vendorAccountNumber: string,
        email: string,
        vendorName: string }>>> {
        return this.httpClient.post<GenericResponseDto<Array<
        { vendorAccountNumber: string,
            email: string,
            vendorName: string }>>>(
            `${environment.apiBaseUrl}/Vendor/maincontacts`,
            { vendorAccountNumbers },
            this.httpOptions
        );
    }

    /**
     * Send emails to specific vendors
     * @param vendors List of vendors with account numbers and emails
     * @param year Year to include in the email portal URL
     * @returns Observable with the operation result
     */
    emailSpecificVendors(vendors: Array<{ vendorAccountNumber: string, email: string }>, year?: number):
    Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/EmailSpecificVendors`,
            { vendors, year },
            this.httpOptions
        );
    }

    /**
     * Get vendor application status counts grouped by status
     * @param archiveYear Archive year to filter workflows
     * @returns Observable with list of status counts
     */
    getVendorApplicationStatusCounts(archiveYear: number):
    Observable<GenericResponseDto<Array<{ status: string, count: number, archiveYear?: number }>>> {
        return this.httpClient.get<GenericResponseDto<Array<{ status: string, count: number, archiveYear?: number }>>>(
            `${this.apiBaseUrl}/GetVendorApplicationStatusCounts?archiveYear=${archiveYear}`,
            this.httpOptions
        );
    }

    /**
     * Get vendor renewal status counts grouped by renewal status
     * @param renewalYear Renewal year to filter
     * @returns Observable with list of renewal status counts
     */
    getVendorRenewalStatusCounts(renewalYear: number):
    Observable<GenericResponseDto<Array<{ status: string, count: number, renewalYear?: number }>>> {
        return this.httpClient.get<GenericResponseDto<Array<{ status: string, count: number, renewalYear?: number }>>>(
            `${this.apiBaseUrl}/GetVendorRenewalStatusCounts?renewalYear=${renewalYear}`,
            this.httpOptions
        );
    }
}
